import { ExpertReview, KnowledgeUnit } from '../../types'

export interface AgreementResult {
  total: number
  agreed: number
  agreementRate: number
  kappa: number
}

/**
 * Compute agreement between LLM proposed_type and expert corrected_type.
 * Uses the 'corrected_label' or 'corrected_type' dimension from reviews.
 * If the expert did not correct, treats the LLM label as agreed.
 */
export function llmExpertAgreement(
  kus: KnowledgeUnit[],
  reviews: ExpertReview[],
  correctnessDimension: string,
  correctedTypeDimension: string,
  taxonomyTypes: string[],
): AgreementResult {
  const reviewByKU = new Map<string, ExpertReview>()
  for (const r of reviews) reviewByKU.set(r.ku_id, r)

  const pairs: { llm: string; expert: string }[] = []
  for (const ku of kus) {
    const review = reviewByKU.get(ku.id)
    if (!review) continue
    const llmType = ku.proposed_type || 'unclear'
    const legacy = review.responses as unknown as Record<string, string | boolean | null>
    const correctness = legacy[correctnessDimension]
    const correctedType = legacy[correctedTypeDimension] as string | null

    // If expert said correct, expert agrees with LLM
    // If expert said incorrect and provided a corrected type, use that
    const expertType = correctness === 'incorrect' && correctedType ? correctedType : llmType
    pairs.push({ llm: llmType, expert: expertType })
  }

  if (pairs.length === 0) return { total: 0, agreed: 0, agreementRate: 0, kappa: 0 }

  const agreed = pairs.filter((p) => p.llm === p.expert).length
  const agreementRate = agreed / pairs.length

  // Cohen's Kappa
  const n = pairs.length
  const categories = taxonomyTypes
  const llmCounts: Record<string, number> = {}
  const expertCounts: Record<string, number> = {}
  for (const cat of categories) {
    llmCounts[cat] = 0
    expertCounts[cat] = 0
  }
  for (const p of pairs) {
    llmCounts[p.llm] = (llmCounts[p.llm] || 0) + 1
    expertCounts[p.expert] = (expertCounts[p.expert] || 0) + 1
  }

  const pe = categories.reduce((sum, cat) => {
    return sum + ((llmCounts[cat] || 0) / n) * ((expertCounts[cat] || 0) / n)
  }, 0)

  const kappa = pe === 1 ? 1 : (agreementRate - pe) / (1 - pe)

  return { total: pairs.length, agreed, agreementRate, kappa }
}
